import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Loader2, Link2, CheckCircle, XCircle, Layers, TrendingUp,
  DollarSign, MousePointerClick, Eye, Target, RefreshCw, Trash2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_COLORS = {
  Google: 'bg-blue-50 text-blue-700 border-blue-200',
  Microsoft: 'bg-teal-50 text-teal-700 border-teal-200',
  MSN: 'bg-teal-50 text-teal-700 border-teal-200',
  Facebook: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  LinkedIn: 'bg-sky-50 text-sky-700 border-sky-200',
  Linkedin: 'bg-sky-50 text-sky-700 border-sky-200',
  TikTok: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  Tiktok: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  YouTube: 'bg-red-50 text-red-700 border-red-200',
  Pinterest: 'bg-rose-50 text-rose-700 border-rose-200',
  Amazon: 'bg-amber-50 text-amber-700 border-amber-200',
  Universal: 'bg-violet-50 text-violet-700 border-violet-200',
  Unknown: 'bg-gray-50 text-gray-600 border-gray-200',
};

const formatMoney = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatInt = (n) => (n || 0).toLocaleString('en-US');

// ═══════════════ PERFORMANCE VIEW ═══════════════
const ProfilePerformance = ({ profile, accounts, onRefreshMappings }) => {
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [mapping, setMapping] = useState(profile.mapping || null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/skai/profiles/${profile.profile_id}/performance?days=${days}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPerf(await res.json());
    } catch (e) {
      toast.error(`Performance load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [profile.profile_id, days]);

  useEffect(() => { load(); }, [load]);

  const mapToAccount = async (accountId, label) => {
    try {
      const res = await authFetch(`${API}/api/skai/mappings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_id: profile.profile_id, account_id: accountId, label }),
      });
      if (!res.ok) throw new Error('Map failed');
      const m = await res.json();
      setMapping(m);
      toast.success('Mapped to optimization account');
      onRefreshMappings();
    } catch (e) { toast.error(e.message); }
  };

  const unmap = async () => {
    try {
      await authFetch(`${API}/api/skai/mappings/${profile.profile_id}`, { method: 'DELETE' });
      setMapping(null);
      toast.success('Mapping removed');
      onRefreshMappings();
    } catch (e) { toast.error(e.message); }
  };

  // Memoize expensive sorts / slices so re-renders don't redo the work on
  // every state change (selecting a date range, mapping an account, etc.).
  // Hooks must run on every render before any early return — guards inside.
  const channels = useMemo(
    () => Object.entries(perf?.by_channel || {}).sort((a, b) => b[1].cost - a[1].cost),
    [perf],
  );
  const topCampaigns = useMemo(
    () => [...(perf?.campaigns || [])].sort((a, b) => b.cost - a.cost).slice(0, 10),
    [perf],
  );

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-brand" /></div>;
  if (!perf) return null;

  const { total } = perf;

  return (
    <div className="space-y-4" data-testid={`perf-${profile.profile_id}`}>
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            className="text-xs border border-border-subtle rounded px-2 py-1 bg-white"
            value={days}
            onChange={e => setDays(parseInt(e.target.value))}
            data-testid="skai-days-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button size="sm" variant="ghost" onClick={load} data-testid="skai-refresh-btn">
            <RefreshCw size={12} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {mapping ? (
            <>
              <span className="text-xs text-emerald-600 flex items-center gap-1">
                <CheckCircle size={12} /> Mapped: {mapping.label}
              </span>
              <Button size="sm" variant="ghost" onClick={unmap} data-testid={`unmap-${profile.profile_id}`}>
                <Trash2 size={12} className="text-red-500" />
              </Button>
            </>
          ) : accounts.length > 0 ? (
            <select
              className="text-xs border border-border-subtle rounded px-2 py-1 bg-white"
              onChange={e => {
                const a = accounts.find(x => x.id === e.target.value);
                if (a) mapToAccount(a.id, a.name);
              }}
              defaultValue=""
              data-testid={`map-select-${profile.profile_id}`}
            >
              <option value="" disabled>Map to account…</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          ) : (
            <span className="text-xs text-text-tertiary">No Ventas accounts to map</span>
          )}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={DollarSign} label="Spend" value={formatMoney(total.cost)} />
        <Stat icon={MousePointerClick} label="Clicks" value={formatInt(total.clicks)} />
        <Stat icon={Eye} label="Impressions" value={formatInt(total.impressions)} />
        <Stat icon={Target} label="Conversions" value={formatInt(Math.round(total.conversion))} />
      </div>

      {/* By channel */}
      {channels.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
            By Channel ({channels.length})
          </p>
          <div className="space-y-1.5">
            {channels.map(([name, m]) => {
              const pct = total.cost > 0 ? (m.cost / total.cost) * 100 : 0;
              return (
                <div key={name} className="flex items-center gap-3" data-testid={`channel-${name}`}>
                  <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded border ${CHANNEL_COLORS[name] || CHANNEL_COLORS.Unknown}`}>
                    {name}
                  </span>
                  <div className="flex-1 h-2 bg-surface-page rounded overflow-hidden">
                    <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-text-secondary tabular-nums w-32 text-right">
                    {formatMoney(m.cost)} • {m.campaigns} camp
                  </div>
                  <div className="text-xs text-text-tertiary tabular-nums w-20 text-right">
                    {formatInt(Math.round(m.conversion))} conv
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top campaigns */}
      {campaigns.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
            Top Campaigns by Spend ({Math.min(10, campaigns.length)} of {campaigns.length})
          </p>
          <div className="space-y-1">
            {topCampaigns.map((c) => (
              <div key={`${c.channel_type}-${c.campaign_name}`} className="flex items-center gap-2 text-xs py-1.5 border-b border-border-subtle" data-testid={`skai-camp-${c.campaign_name}`}>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${CHANNEL_COLORS[c.channel_type] || CHANNEL_COLORS.Unknown}`}>
                  {c.channel_type}
                </span>
                <span className="flex-1 truncate text-text-primary">{c.campaign_name}</span>
                <span className="tabular-nums text-text-secondary w-20 text-right">{formatMoney(c.cost)}</span>
                <span className="tabular-nums text-text-tertiary w-16 text-right">{formatInt(c.clicks)} clk</span>
                <span className="tabular-nums text-text-tertiary w-16 text-right">{formatInt(Math.round(c.conversion))} conv</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-surface-page rounded-lg p-3">
    <div className="flex items-center gap-2 text-text-tertiary mb-1">
      <Icon size={12} />
      <span className="text-[10px] uppercase font-medium">{label}</span>
    </div>
    <p className="text-lg font-bold text-text-primary tabular-nums">{value}</p>
  </div>
);

// ═══════════════ PROFILE CARD ═══════════════
const ProfileCard = ({ profile, expanded, onToggle, accounts, onRefreshMappings }) => {
  return (
    <Card data-testid={`skai-profile-${profile.profile_id}`}>
      <CardContent className="p-0">
        <button
          onClick={onToggle}
          className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-surface-page transition-colors"
        >
          <div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
            <Layers size={16} className="text-brand" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text-primary">
              Profile {profile.profile_id}
              {profile.mapping && (
                <span className="ml-2 text-xs font-normal text-emerald-600">— {profile.mapping.label}</span>
              )}
            </p>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {profile.channel_types.map(ch => (
                <span key={ch} className={`text-[10px] font-medium uppercase px-1.5 py-0.5 rounded border ${CHANNEL_COLORS[ch] || CHANNEL_COLORS.Unknown}`}>
                  {ch}
                </span>
              ))}
              <span className="text-xs text-text-tertiary ml-1">{profile.campaign_count} campaigns</span>
            </div>
          </div>
          <TrendingUp size={14} className={`text-text-muted transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {expanded && (
          <div className="px-4 pb-4 pt-0 border-t border-border-subtle">
            <ProfilePerformance profile={profile} accounts={accounts} onRefreshMappings={onRefreshMappings} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ═══════════════ MAIN SKAI PANEL ═══════════════
const OptimizationSkai = ({ accounts }) => {
  const [status, setStatus] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [showMappedOnly, setShowMappedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        authFetch(`${API}/api/skai/status`),
        authFetch(`${API}/api/skai/profiles`),
      ]);
      if (sRes.ok) setStatus(await sRes.json());
      if (pRes.ok) {
        const d = await pRes.json();
        setProfiles(d.profiles || []);
      } else if (pRes.status === 400) {
        setProfiles([]);
      }
    } catch (e) { console.error('Skai load:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Hooks must run on every render — declare before any early return.
  const sortedChannelTotals = useMemo(() => {
    const totals = {};
    profiles.forEach(p => p.channel_types.forEach(c => {
      totals[c] = (totals[c] || 0) + 1;
    }));
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [profiles]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand" size={24} /></div>;

  if (!status?.configured) {
    return (
      <Card className="py-12 text-center" data-testid="skai-not-configured">
        <Link2 size={40} className="mx-auto text-text-muted mb-3" />
        <h3 className="text-lg font-semibold text-text-primary mb-1">Skai not configured</h3>
        <p className="text-sm text-text-tertiary max-w-md mx-auto">
          Set SKAI_REFRESH_TOKEN, SKAI_CLIENT_ID and SKAI_KS_ID in the backend environment to enable multi-channel paid-search data.
        </p>
      </Card>
    );
  }

  if (!status?.connection_healthy) {
    return (
      <Card className="py-8 text-center" data-testid="skai-unhealthy">
        <XCircle size={32} className="mx-auto text-red-500 mb-3" />
        <h3 className="text-base font-semibold text-text-primary mb-1">Skai connection failed</h3>
        <p className="text-sm text-text-tertiary mb-3">{status.error || 'Refresh token may have been revoked.'}</p>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw size={12} className="mr-1" /> Retry</Button>
      </Card>
    );
  }

  const filtered = profiles
    .filter(p => !showMappedOnly || p.mapping)
    .filter(p => !search ||
      String(p.profile_id).includes(search) ||
      (p.mapping?.label || '').toLowerCase().includes(search.toLowerCase()) ||
      p.channel_types.some(c => c.toLowerCase().includes(search.toLowerCase()))
    );

  const mappedCount = profiles.filter(p => p.mapping).length;

  return (
    <div className="space-y-5" data-testid="skai-panel">
      {/* Connection header */}
      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="p-3 flex items-center gap-3">
          <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-900">Connected to Skai</p>
            <p className="text-xs text-emerald-700">
              KS ID <b>{status.ks_id}</b> • {profiles.length} profiles • {mappedCount} mapped to Ventas accounts
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={load} data-testid="skai-reload-btn">
            <RefreshCw size={14} />
          </Button>
        </CardContent>
      </Card>

      {/* Channel breakdown */}
      <div className="flex items-center gap-2 flex-wrap">
        {sortedChannelTotals.map(([ch, n]) => (
          <span key={ch} className={`text-xs font-medium px-2 py-1 rounded border ${CHANNEL_COLORS[ch] || CHANNEL_COLORS.Unknown}`}>
            {ch} · {n}
          </span>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Search by profile ID, label, or channel…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 text-sm border border-border-subtle rounded px-3 py-1.5 bg-white"
          data-testid="skai-search-input"
        />
        <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={showMappedOnly}
            onChange={e => setShowMappedOnly(e.target.checked)}
            data-testid="skai-mapped-only-toggle"
          />
          Mapped only
        </label>
      </div>

      {/* Profiles list */}
      {filtered.length === 0 ? (
        <Card className="py-8 text-center">
          <p className="text-sm text-text-tertiary">No profiles match your filter.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, 50).map(p => (
            <ProfileCard
              key={p.profile_id}
              profile={p}
              accounts={accounts}
              expanded={expanded === p.profile_id}
              onToggle={() => setExpanded(expanded === p.profile_id ? null : p.profile_id)}
              onRefreshMappings={load}
            />
          ))}
          {filtered.length > 50 && (
            <p className="text-xs text-text-tertiary text-center py-2">
              Showing first 50 of {filtered.length}. Use search to narrow.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OptimizationSkai;
